import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useScrollHeader from '../../hooks/useScrollHeader';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isFloating = useScrollHeader();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  const handleSectionClick = (sectionId) => {
    closeMobileMenu();
    
    // If we're already on the home page, just scroll to section
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If we're on another page, navigate to home first, then scroll to section
      navigate('/');
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isFloating 
        ? 'floating-header' 
        : 'bg-white shadow-premium rounded-b-3xl'
    }`}>
      <div className="container nav-container">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-4 hover:scale-105 transition-transform duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                <img 
                  src="/img/dr.Adesh.Palyekar.png" 
                  alt="Dr. Adesh Palyekar" 
                  className="w-8 h-8 rounded-full object-cover"
                />
              </div>
              <div>
                <div className="font-display text-xl font-bold text-primary-700">
                  Dr. Adesh Palyekar
                </div>
                <div className="text-sm text-text-muted">
                  Cancer Surgeon
                </div>
              </div>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <button onClick={() => handleSectionClick('services')} className="nav-link">Services</button>
            <button onClick={() => handleSectionClick('locations')} className="nav-link">Locations</button>
            <button onClick={() => handleSectionClick('testimonials')} className="nav-link">Testimonials</button>
            <Link to="/blog" className="nav-link">Blog</Link>
            <button onClick={() => handleSectionClick('contact')} className="btn-primary">Contact</button>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMobileMenu}
            className="lg:hidden w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center text-white hover:shadow-premium transition-all duration-300 z-50"
            aria-label="Toggle mobile menu"
          >
            <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
          </button>
        </div>
        
        {/* Mobile Navigation */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-premium-lg border-t border-primary-100 transition-all duration-300 ${
          isMobileMenuOpen 
            ? 'max-h-screen opacity-100 visible' 
            : 'max-h-0 opacity-0 invisible'
        }`}>
          <div className="container py-4 space-y-2">
            <button onClick={() => handleSectionClick('services')} className="mobile-nav-link">Services</button>
            <button onClick={() => handleSectionClick('locations')} className="mobile-nav-link">Locations</button>
            <button onClick={() => handleSectionClick('testimonials')} className="mobile-nav-link">Testimonials</button>
            <Link to="/blog" className="mobile-nav-link" onClick={closeMobileMenu}>Blog</Link>
            <button onClick={() => handleSectionClick('contact')} className="mobile-nav-link bg-accent-500 text-white">Contact</button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 